import { useState } from 'react';
import { Link } from 'react-router-dom';
import { orgApi, type Organization } from '../../api/organizations';

export function OrganizationSearchPage() {
  const [query, setQuery] = useState('');
  const [legalName, setLegalName] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [gstNumber, setGstNumber] = useState('');
  const [status, setStatus] = useState('');
  const [tags, setTags] = useState('');
  const [results, setResults] = useState<Organization[]>([]);
  const [count, setCount] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function search() {
    setLoading(true);
    setError('');
    try {
      const params: Record<string, string> = {};
      if (query.trim()) params.q = query.trim();
      if (legalName.trim()) params.legal_name = legalName.trim();
      if (registrationNumber.trim()) params.registration_number = registrationNumber.trim();
      if (gstNumber.trim()) params.gst_number = gstNumber.trim();
      if (status) params.status = status;
      if (tags.trim()) params.tags = tags.trim();
      const res = await orgApi.search(params);
      setResults(res.results ?? []);
      setCount(res.count ?? 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mx-auto max-w-6xl p-6">
      <Link to="/organizations" className="text-sm text-slate-600 hover:underline">
        ← Organizations
      </Link>
      <h1 className="mt-4 text-2xl font-semibold text-slate-900">Organization Search</h1>
      <p className="mt-1 text-sm text-slate-600">Search by name, legal identity, GST, status, or tags.</p>

      {error && <p className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}

      <div className="mt-6 grid gap-3 rounded-xl border border-slate-200 bg-white p-4 md:grid-cols-2">
        <input
          placeholder="Query"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="rounded-lg border border-slate-300 px-3 py-2"
        />
        <input
          placeholder="Legal name"
          value={legalName}
          onChange={(e) => setLegalName(e.target.value)}
          className="rounded-lg border border-slate-300 px-3 py-2"
        />
        <input
          placeholder="Registration number"
          value={registrationNumber}
          onChange={(e) => setRegistrationNumber(e.target.value)}
          className="rounded-lg border border-slate-300 px-3 py-2"
        />
        <input
          placeholder="GST number"
          value={gstNumber}
          onChange={(e) => setGstNumber(e.target.value)}
          className="rounded-lg border border-slate-300 px-3 py-2"
        />
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2">
          <option value="">Any status</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="archived">Archived</option>
        </select>
        <input
          placeholder="Tags (comma-separated)"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          className="rounded-lg border border-slate-300 px-3 py-2"
        />
        <button
          type="button"
          onClick={() => void search()}
          className="rounded-lg bg-slate-900 px-4 py-2 text-white md:col-span-2 md:w-fit"
        >
          {loading ? 'Searching…' : 'Search'}
        </button>
      </div>

      <p className="mt-4 text-sm text-slate-500">{count} results</p>
      <ul className="mt-4 space-y-3">
        {results.map((org) => (
          <li key={org.id} className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <Link to={`/organizations/${org.id}`} className="font-medium text-slate-900 hover:underline">
                  {org.name}
                </Link>
                <p className="text-sm text-slate-600">{org.slug}</p>
                {org.profile?.legal_name && <p className="text-sm text-slate-500">{org.profile.legal_name}</p>}
              </div>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs capitalize text-slate-700">{org.status}</span>
            </div>
            <Link to={`/organizations/${org.id}/dashboard`} className="mt-2 inline-block text-xs text-slate-600 hover:underline">
              Dashboard
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
